import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';
import classnames from 'classnames';
import Popup from 'views/shared/ui/popup';
import { t } from 'services/translation';
import '../index.scss';
import { isActionKey } from 'ui_system/utilities/KeyboardHelpers';
import {
  isTicketSoldOut,
  getTicketCapacityLimit,
  isAABandSoldOutText,
} from '../helpers/ticketCapacityHelpers';

class MultiSelectOption extends React.Component {
  static propTypes = {
    id: PropTypes.number,
    quantity: PropTypes.number,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    price: PropTypes.string,
    inPlaceEdit: PropTypes.bool,
    date: PropTypes.string,
    time: PropTypes.string,
    addonAvailability: PropTypes.object,
    onExpandIdChange: PropTypes.func,
    expandedId: PropTypes.string,
    hasTicketCapacityEnabled: PropTypes.bool,
    slotTicketCapacity: PropTypes.object,
    isAAB: PropTypes.bool,
    batchUnitsUpdate: PropTypes.func,
  };

  static defaultProps = {
    quantity: 0,
    description: '',
    price: '',
    inPlaceEdit: false,
    date: '',
    time: '',
    addonAvailability: {},
    isAAB: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      showInput: false,
      disableInput: false,
      isTextOverflow: false,
    };
    this.textRef = React.createRef();
  }

  UNSAFE_componentWillMount() {
    this.id = _.uniqueId('multi-select-option-');
  }

  componentDidMount() {
    this._checkTextOverflow();

    const isSoldOut = isTicketSoldOut(this.props);
    if (isSoldOut && this.props.quantity > 0) {
      this.props.batchUnitsUpdate();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.name !== this.props.name) {
      this._checkTextOverflow();
    }

    const addonCapacity = this._calculateAddonCapacity();
    const isSoldOut = isTicketSoldOut(this.props);
    const wasSoldOut = isTicketSoldOut(prevProps);

    if (
      (prevProps.time !== this.props.time ||
        prevProps.date !== this.props.date) &&
      this.overCapacity(addonCapacity)
    ) {
      this.props.onSetQuantityToZero(
        -(this.props.quantity - addonCapacity),
        this.props.id
      );
    }

    if (isSoldOut && !wasSoldOut && this.props.quantity > 0) {
      this.props.batchUnitsUpdate();
    }
  }

  _checkTextOverflow() {
    const el = this.textRef.current;
    if (el) {
      const isOverflowing =
        el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
      if (isOverflowing !== this.state.isTextOverflow) {
        this.setState({ isTextOverflow: isOverflowing });
      }
    }
  }

  overCapacity(addonCapacity) {
    if (!addonCapacity) return false;
    return this.props.quantity > addonCapacity;
  }

  onDecrease = (e) => {
    const isSoldOut = isTicketSoldOut(this.props);
    if (isSoldOut) {
      e.preventDefault();
      return;
    }
    if (this.props.quantity > 0) {
      this.props.onDecrease(this.props.id);
    }
  };

  onIncrease = (e) => {
    const isSoldOut = isTicketSoldOut(this.props);
    if (isSoldOut) {
      e.preventDefault();
      return;
    }

    const addonCapacity = this._calculateAddonCapacity();
    if (addonCapacity === null || this.props.quantity < addonCapacity) {
      this.props.onIncrease(this.props.id);
    }
  };

  _popupDescription() {
    return (
      <div className="description-tool-tip">
        <div
          id={`${this.id}-tooltip-sub`}
          className="subcategory tooltip-subcategory"
        >
          {this.props.name}
        </div>
        <div id={`${this.id}-tooltip-desc`} className="description">
          {this.props.description}
        </div>
      </div>
    );
  }

  _handleQuantityClick(e) {
    if (this.props.inPlaceEdit) {
      this.setState({ showInput: true });
      setTimeout(() => {
        this.qInput.focus();
        this.qInput.select();
      });
    }
  }

  inputBlurred = (e) => {
    this.setState({ showInput: false });
  };

  inputKeyPress = (e) => {
    if (e.key === 'Enter') {
      this.setState({ showInput: false });
    }
  };

  inputChanged = (e) => {
    let newValue = e.target.value;
    if (newValue > -1) {
      if (newValue === '') {
        newValue = 0;
      }
      const totalChange = this.props.quantity - newValue;
      this.props.onChange(this.props.id, parseInt(newValue, 10), totalChange);
    }
  };

  _formatPrice(price) {
    if (!price) {
      return null;
    }

    const currencySymbol = price.substring(0, 1);
    let priceVal = price.substring(1);
    if (priceVal % 1 === 0) {
      priceVal = parseInt(priceVal, 10);
    }
    if (priceVal === 0) {
      return t('FREE');
    }
    return currencySymbol + priceVal;
  }

  _formatAddOnCapacity() {
    if (this.props.addon && this.props.addon.max_capacity) {
      return (
        <span data-testid="addon-availability" className="add-on-availability">
          {this._calculateAddonCapacity()} available
        </span>
      );
    }
  }

  _renderToolTip() {
    const isTextOverflow = this.state.isTextOverflow;
    const classNames = classnames('description-tool-tip-target', {
      'is-text-overflow': isTextOverflow,
    });
    return [
      <span
        id={this.id}
        className={classNames}
        role="button"
        tabIndex="0"
        aria-label={`${this.props.name} ticket tooltip`}
        aria-describedby={`${this.id}-tooltip-sub ${this.id}-tooltip-desc`}
        aria-expanded={this.id == this.props.expandedId}
      >
        <i className="icon af-help-circle" />
      </span>,
      <Popup
        content={this._popupDescription()}
        target={`#${this.id}.description-tool-tip-target`}
        position="bottom left"
        onPopupClose={() => this.props.onExpandIdChange('')}
        onPopupOpen={() => this.props.onExpandIdChange(this.id)}
        identifier={`popup-${this.id}`}
      />,
    ];
  }

  _calculateAddonCapacity() {
    const addonAvailability = this.props.addonAvailability;
    const addon = this.props.addon;
    const date = this.props.date;
    const time = this._formatTimeCorrectly();

    if (
      addon &&
      addon.max_capacity &&
      addon.id in addonAvailability &&
      date in addonAvailability[addon.id] &&
      time in addonAvailability[addon.id][date]
    ) {
      return addon.max_capacity - addonAvailability[addon.id][date][time];
    } else if (addon && addon.max_capacity) {
      return addon.max_capacity;
    }

    return null;
  }

  _formatTimeCorrectly() {
    const { time } = this.props;
    if (!time) {
      return '';
    }
    let baseTime = time;
    if (typeof time === 'object' && time.label) {
      baseTime = time.label;
    }
    baseTime = baseTime.toLowerCase().replace(/\s/g, '');
    let hours = Number(baseTime.split(':')[0]);
    const suffix = hours >= 12 ? 'pm' : 'am';
    hours = ((hours + 11) % 12) + 1;
    hours = hours.toString().length === 1 ? `0${hours}` : hours;
    return baseTime.includes('am') || baseTime.includes('pm')
      ? `${hours}:${baseTime.split(':')[1]}`
      : `${hours}:${baseTime.split(':')[1]}${suffix}`;
  }

  _addonControls() {
    const addonCapacity = this._calculateAddonCapacity();
    const { quantity, inPlaceEdit, increasable, decreasable } = this.props;
    const { showInput } = this.state;
    const capacityLimit = getTicketCapacityLimit(this.props);
    const isSoldOut = isTicketSoldOut(this.props);

    const decreaseControlClasses = classnames('ui', 'control', {
      disabled: !decreasable || quantity === 0 || isSoldOut,
    });
    const increaseControlClasses = classnames('ui', 'control', {
      disabled: !increasable || quantity === addonCapacity || isSoldOut,
    });
    const quantityValueClasses = classnames('quantity', {
      hide: showInput,
      hoverable: inPlaceEdit,
    });
    const quantityInputClasses = classnames('quantity-input', {
      hide: !showInput,
    });

    return (
      <div className="controls">
        <button
          type="button"
          aria-label={`Decrease ${this.props.name}`}
          data-testid="quantity-decrease"
          className={decreaseControlClasses}
          onClick={this.onDecrease.bind(this)}
          onKeyDown={(e) => isActionKey(e.key) && this.onDecrease.bind(this)}
        >
          <i className="af-dash brand-button-color" />
        </button>

        <button
          type="button"
          onClick={this._handleQuantityClick.bind(this)}
          onKeyDown={(e) =>
            isActionKey(e.key) && this._handleQuantityClick.bind(this)
          }
          className={quantityValueClasses}
          data-testid="guest-count"
        >
          {!isSoldOut ? quantity : 0}
        </button>
        {inPlaceEdit && (
          <input
            type="number"
            className={quantityInputClasses}
            ref={(input) => {
              this.qInput = input;
            }}
            value={quantity}
            onChange={this.inputChanged.bind(this)}
            onBlur={this.inputBlurred.bind(this)}
            onKeyPress={this.inputKeyPress.bind(this)}
          />
        )}
        <button
          type="button"
          aria-label={`Increase ${this.props.name}`}
          data-testid="quantity-increase"
          className={increaseControlClasses}
          onClick={this.onIncrease.bind(this)}
          onKeyDown={(e) => isActionKey(e.key) && this.onIncrease.bind(this)}
        >
          <i className="af-plus brand-button-color" />
        </button>
        <div className="ticket-capacity-limit">
          {/* Capacity limit message AND sold out message for guest page */}
          {capacityLimit && (
            <div className="capacity-limit-message">
              {t(`LIMIT`, '', { numLimit: capacityLimit.limit })}
            </div>
          )}
          {isSoldOut && (
            <div className="capacity-limit-message">{t('SOLD_OUT')}</div>
          )}
        </div>
      </div>
    );
  }

  render() {
    const multiSelectOptionClasses = classnames('ui', 'multi-select-option');
    const aabSoldOutMessage = isAABandSoldOutText(this.props);

    return (
      <div ref="multiSelectOption" className={multiSelectOptionClasses}>
        <div className="head">
          <div className="subcategory">
            <span
              ref={this.textRef}
              className="subcategory-text"
              title={t(this.props.name)}
            >
              {t(this.props.name)}
            </span>
            {this.props.renderToolTip || this.state.isTextOverflow
              ? this._renderToolTip()
              : null}
          </div>
          {this._addonControls()}
        </div>
        <div className="pricing-breakdown">
          <span className="description" data-testid="pricing-description">
            {t(this.props.description, null, null, false)}
          </span>
          {` @ `}
          {this._formatPrice(this.props.price)}
          {this._formatAddOnCapacity()}
        </div>
        {/* Capacity limit message AND sold out message for AAB page */}
        {aabSoldOutMessage && (
          <div className="capacity-limit-message-for-aab">
            {aabSoldOutMessage}
          </div>
        )}
      </div>
    );
  }
}

export default MultiSelectOption;
